import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { FilterBar } from '@/components/ui/FilterBar';
import { api } from '@/api/client';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';
import { exportToCSV, exportToExcel } from '@/utils/exportUtils';
import { Eye, DollarSign, AlertTriangle, FileText, FileSpreadsheet, Printer } from 'lucide-react';

const fmtDate = (s) => (s ? String(s).slice(0, 10) : '-');

const creditBadge = (status) => {
  switch (status) {
    case 'Paid': return 'success';
    case 'Partially Paid': return 'warning';
    case 'Overdue': return 'danger';
    case 'Unpaid': return 'info';
    default: return 'default';
  }
};

export const DealerCredit = () => {
  const currency = useCurrency();
  const [data, setData] = useState({ summary: null, rows: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [onlyOverdue, setOnlyOverdue] = useState(false);
  const [statement, setStatement] = useState(null);
  const [paymentInv, setPaymentInv] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.getDealersOutstanding();
      setData(res.data);
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return (data.rows || []).filter((r) => {
      if (onlyOverdue && (r.overdue || 0) <= 0) return false;
      if (!q) return true;
      return (r.customer_name + ' ' + r.phone).toLowerCase().includes(q);
    });
  }, [data, search, onlyOverdue]);

  const openStatement = async (row) => {
    try {
      const res = await api.getCustomerStatement(row.customer_id);
      setStatement(res.data);
    } catch (e) { alert('Failed to load statement'); }
  };

  return (
    <div className="space-y-6" data-testid="dealer-credit-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold">Dealer Credit / Accounts Receivable</h1>
          <p className="text-sm text-[var(--text-muted)]">Outstanding balances and overdue amounts per customer.</p>
        </div>
      </div>

      {/* KPIs */}
      {data.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card>
            <div className="text-xs text-[var(--text-muted)] uppercase">Total Outstanding</div>
            <div className="text-2xl font-bold text-blue-600 mt-1" data-testid="kpi-outstanding">{formatCurrency(data.summary.total_outstanding, currency)}</div>
          </Card>
          <Card>
            <div className="text-xs text-[var(--text-muted)] uppercase">Overdue</div>
            <div className="text-2xl font-bold text-red-600 mt-1" data-testid="kpi-overdue">{formatCurrency(data.summary.total_overdue, currency)}</div>
          </Card>
          <Card>
            <div className="text-xs text-[var(--text-muted)] uppercase">Active Dealers</div>
            <div className="text-2xl font-bold mt-1" data-testid="kpi-dealers">{data.summary.dealer_count}</div>
          </Card>
          <Card>
            <div className="text-xs text-[var(--text-muted)] uppercase">Customers w/ Outstanding</div>
            <div className="text-2xl font-bold mt-1" data-testid="kpi-customers">{data.summary.customer_count}</div>
          </Card>
        </div>
      )}

      <Card>
        <FilterBar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search dealer / customer..."
          filters={[]}
          onClear={() => { setSearch(''); setOnlyOverdue(false); }}
          onExport={() => exportToCSV(filtered, 'dealer_credit', [
            { key: 'customer_name', label: 'Customer' },
            { key: 'phone', label: 'Phone' },
            { key: 'is_dealer', label: 'Dealer' },
            { key: 'invoice_count', label: 'Invoices' },
            { key: 'total_purchases', label: 'Total Purchases' },
            { key: 'total_paid', label: 'Total Paid' },
            { key: 'outstanding', label: 'Outstanding' },
            { key: 'overdue', label: 'Overdue' },
            { key: 'credit_limit', label: 'Credit Limit' },
          ])}
          testId="dealer-credit-filter"
        />
        <div className="flex items-center gap-2 mb-2">
          <label className="text-xs flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={onlyOverdue} onChange={(e) => setOnlyOverdue(e.target.checked)} data-testid="filter-overdue-only" />
            <span>Show only overdue</span>
          </label>
        </div>

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Customer</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Invoices</TableHead>
              <TableHead>Total Purchases</TableHead>
              <TableHead>Total Paid</TableHead>
              <TableHead>Outstanding</TableHead>
              <TableHead>Overdue</TableHead>
              <TableHead>Credit Limit</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={10} className="text-center py-8 text-gray-500">No outstanding balances 🎉</TableCell></TableRow>
            ) : filtered.map((r) => {
              const overLimit = r.credit_limit > 0 && r.outstanding > r.credit_limit;
              return (
                <TableRow key={r.customer_id}>
                  <TableCell className="font-medium">{r.customer_name}</TableCell>
                  <TableCell className="text-xs">{r.phone}</TableCell>
                  <TableCell>
                    {r.is_dealer ? <Badge variant="info">Dealer</Badge> : <Badge variant="default">Customer</Badge>}
                  </TableCell>
                  <TableCell>{r.invoice_count}</TableCell>
                  <TableCell>{formatCurrency(r.total_purchases, currency)}</TableCell>
                  <TableCell className="text-emerald-600">{formatCurrency(r.total_paid, currency)}</TableCell>
                  <TableCell className="font-semibold">{formatCurrency(r.outstanding, currency)}</TableCell>
                  <TableCell className={r.overdue > 0 ? 'font-semibold text-red-600' : ''}>
                    {r.overdue > 0 && <AlertTriangle size={12} className="inline mr-1" />}
                    {formatCurrency(r.overdue, currency)}
                  </TableCell>
                  <TableCell>
                    <div className="text-xs">
                      {r.credit_limit > 0 ? formatCurrency(r.credit_limit, currency) : '—'}
                    </div>
                    {overLimit && <div className="text-xs text-red-600 font-semibold">OVER LIMIT</div>}
                  </TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => openStatement(r)} title="View statement" data-testid={`view-stmt-${r.customer_id}`}>
                      <Eye size={14} />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {statement && (
        <StatementModal
          statement={statement}
          currency={currency}
          onClose={() => setStatement(null)}
          onRecordPayment={(inv) => setPaymentInv({ invoice: inv, statement })}
        />
      )}

      {paymentInv && (
        <PaymentModal
          invoice={paymentInv.invoice}
          onClose={() => setPaymentInv(null)}
          onSaved={async () => {
            // Reload both list & current statement
            await load();
            try {
              const res = await api.getCustomerStatement(paymentInv.statement.customer.id);
              setStatement(res.data);
            } catch (e) { /* ignore */ }
            setPaymentInv(null);
          }}
        />
      )}
    </div>
  );
};

// ===================== Statement Modal =====================
const StatementModal = ({ statement, currency, onClose, onRecordPayment }) => {
  const { customer, summary, invoices } = statement;

  const handleExcel = () => {
    const cols = [
      { key: 'invoice_date', label: 'Date' },
      { key: 'invoice_id', label: 'Invoice ID', format: (v) => String(v).slice(0, 8).toUpperCase() },
      { key: 'payment_type', label: 'Payment Type' },
      { key: 'due_date', label: 'Due Date' },
      { key: 'total', label: 'Total' },
      { key: 'paid_amount', label: 'Paid' },
      { key: 'outstanding', label: 'Outstanding' },
      { key: 'credit_status', label: 'Status' },
      { key: 'days_overdue', label: 'Days Overdue' },
    ];
    exportToExcel(invoices, `statement_${customer.name.replace(/\s+/g, '_')}`, cols, 'Statement');
  };

  const handlePrint = () => {
    const html = `<!DOCTYPE html><html><head><title>Statement — ${customer.name}</title>
    <style>
      body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;padding:30px;color:#111;}
      h1{margin:0 0 4px 0;font-size:22px;}
      .muted{color:#666;font-size:12px;margin-bottom:18px;}
      .grid{display:grid;grid-template-columns:repeat(4,1fr);gap:10px;margin-bottom:18px;}
      .card{border:1px solid #ddd;border-radius:8px;padding:10px;}
      .card .label{font-size:10px;text-transform:uppercase;color:#777;}
      .card .value{font-size:16px;font-weight:600;margin-top:4px;}
      table{width:100%;border-collapse:collapse;font-size:12px;}
      th,td{border-bottom:1px solid #eee;padding:6px 8px;text-align:left;}
      th{background:#f7f7f7;}
      .neg{color:#b91c1c;} .pos{color:#047857;}
      @media print{@page{margin:14mm;}}
    </style></head><body>
      <h1>Statement of Account</h1>
      <div class="muted">${customer.name} • ${customer.phone || ''} • Generated ${new Date().toLocaleString()}</div>
      <div class="grid">
        <div class="card"><div class="label">Total Billed</div><div class="value">${formatCurrency(summary.total_billed, currency)}</div></div>
        <div class="card"><div class="label">Total Paid</div><div class="value pos">${formatCurrency(summary.total_paid, currency)}</div></div>
        <div class="card"><div class="label">Outstanding</div><div class="value neg">${formatCurrency(summary.outstanding, currency)}</div></div>
        <div class="card"><div class="label">Overdue</div><div class="value neg">${formatCurrency(summary.overdue, currency)}</div></div>
      </div>
      <table><thead><tr>
        <th>Date</th><th>Invoice</th><th>Type</th><th>Due</th>
        <th style="text-align:right">Total</th><th style="text-align:right">Paid</th><th style="text-align:right">Outstanding</th><th>Status</th></tr></thead>
      <tbody>
        ${invoices.map(i => `<tr>
          <td>${fmtDate(i.invoice_date)}</td>
          <td>${String(i.invoice_id).slice(0,8).toUpperCase()}</td>
          <td>${i.payment_type || '-'}</td>
          <td>${i.due_date || '-'}</td>
          <td style="text-align:right">${formatCurrency(i.total, currency)}</td>
          <td style="text-align:right">${formatCurrency(i.paid_amount, currency)}</td>
          <td style="text-align:right">${formatCurrency(i.outstanding, currency)}</td>
          <td>${i.credit_status}${i.days_overdue > 0 ? ` (${i.days_overdue}d)` : ''}</td>
        </tr>`).join('')}
      </tbody></table>
      <script>window.onload=()=>{setTimeout(()=>window.print(),250);};</script>
    </body></html>`;
    const w = window.open('', '_blank', 'width=900,height=700');
    if (!w) { alert('Pop-up blocked. Allow pop-ups to print.'); return; }
    w.document.write(html);
    w.document.close();
  };

  return (
    <Modal isOpen onClose={onClose} title={`Statement — ${customer.name}`} size="xl">
      <div className="space-y-4" data-testid="statement-modal">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card><div className="text-xs text-[var(--text-muted)]">Total Billed</div><div className="text-xl font-bold">{formatCurrency(summary.total_billed, currency)}</div></Card>
          <Card><div className="text-xs text-[var(--text-muted)]">Paid</div><div className="text-xl font-bold text-emerald-600">{formatCurrency(summary.total_paid, currency)}</div></Card>
          <Card><div className="text-xs text-[var(--text-muted)]">Outstanding</div><div className="text-xl font-bold text-blue-600">{formatCurrency(summary.outstanding, currency)}</div></Card>
          <Card><div className="text-xs text-[var(--text-muted)]">Overdue</div><div className="text-xl font-bold text-red-600">{formatCurrency(summary.overdue, currency)}</div></Card>
        </div>

        {summary.credit_limit > 0 && (
          <div className="text-xs">
            <strong>Credit Limit:</strong> {formatCurrency(summary.credit_limit, currency)} ·
            <strong> Available:</strong> <span className={summary.available_credit < 0 ? 'text-red-600' : 'text-emerald-600'}>{formatCurrency(summary.available_credit, currency)}</span>
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <Button size="sm" variant="outline" onClick={handleExcel} data-testid="stmt-export-excel"><FileSpreadsheet size={14} className="mr-1" /> Excel</Button>
          <Button size="sm" variant="outline" onClick={handlePrint} data-testid="stmt-print"><Printer size={14} className="mr-1" /> Print / PDF</Button>
        </div>

        <div className="border border-[var(--border)] rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Paid</TableHead>
                <TableHead>Outstanding</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {invoices.length === 0 ? (
                <TableRow><TableCell colSpan={9} className="text-center py-6 text-gray-500">No invoices.</TableCell></TableRow>
              ) : invoices.map((i) => (
                <TableRow key={i.invoice_id}>
                  <TableCell className="text-xs">{fmtDate(i.invoice_date)}</TableCell>
                  <TableCell className="font-mono text-xs">{String(i.invoice_id).slice(0, 8).toUpperCase()}</TableCell>
                  <TableCell className="text-xs">{i.payment_type || '-'}</TableCell>
                  <TableCell className="text-xs">{i.due_date || '-'}</TableCell>
                  <TableCell>{formatCurrency(i.total, currency)}</TableCell>
                  <TableCell className="text-emerald-600">{formatCurrency(i.paid_amount, currency)}</TableCell>
                  <TableCell className="font-semibold">{formatCurrency(i.outstanding, currency)}</TableCell>
                  <TableCell>
                    <Badge variant={creditBadge(i.credit_status)}>
                      {i.credit_status}{i.days_overdue > 0 ? ` ${i.days_overdue}d` : ''}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {i.outstanding > 0 && (
                      <Button size="sm" variant="ghost" onClick={() => onRecordPayment(i)} title="Record payment" data-testid={`record-payment-${i.invoice_id}`}>
                        <DollarSign size={14} className="text-emerald-600" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    </Modal>
  );
};

// ===================== Record Payment Modal =====================
const PaymentModal = ({ invoice, onClose, onSaved }) => {
  const currency = useCurrency();
  const [form, setForm] = useState({
    amount: invoice.outstanding,
    date: new Date().toISOString().slice(0, 10),
    method: 'Cash',
    notes: ''
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { alert('Enter a valid amount'); return; }
    if (amt > invoice.outstanding + 0.01) { alert(`Amount exceeds outstanding (${formatCurrency(invoice.outstanding, currency)})`); return; }
    setSaving(true);
    try {
      await api.recordInvoicePayment(invoice.invoice_id, { ...form, amount: amt });
      onSaved();
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to record payment');
    } finally { setSaving(false); }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Record Payment — Invoice ${String(invoice.invoice_id).slice(0,8).toUpperCase()}`} size="md">
      <form onSubmit={submit} className="space-y-3" data-testid="payment-modal">
        <div className="bg-[var(--surface-hover)] p-3 rounded text-xs space-y-1">
          <div><span className="text-[var(--text-muted)]">Total:</span> <strong>{formatCurrency(invoice.total, currency)}</strong></div>
          <div><span className="text-[var(--text-muted)]">Already paid:</span> {formatCurrency(invoice.paid_amount, currency)}</div>
          <div><span className="text-[var(--text-muted)]">Remaining:</span> <strong className="text-blue-600">{formatCurrency(invoice.outstanding, currency)}</strong></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Amount *" type="number" step="0.01" min="0.01" max={invoice.outstanding}
            value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
            data-testid="payment-amount" />
          <Input label="Date *" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
            data-testid="payment-date" />
        </div>
        <Select label="Method" value={form.method} onChange={(e) => setForm({ ...form, method: e.target.value })} data-testid="payment-method">
          <option>Cash</option>
          <option>Bank Transfer</option>
          <option>Card</option>
          <option>Cheque</option>
          <option>Other</option>
        </Select>
        <Textarea label="Notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="payment-notes" />
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, amount: invoice.outstanding })}>Full Payment</Button>
          <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, amount: invoice.outstanding / 2 })}>Half</Button>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-[var(--border)]">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={saving} data-testid="save-payment"><DollarSign size={14} className="mr-1" /> {saving ? 'Saving...' : 'Record Payment'}</Button>
        </div>
      </form>
    </Modal>
  );
};
